import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import {
  ArrowLeft,
  Users,
  Building2,
  DollarSign,
  TrendingUp,
  RefreshCw,
  Loader2,
  CheckCircle2,
  Clock,
  Download,
  Search,
  Settings,
  Percent,
  FileDown,
  MoreVertical,
  Wallet,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', {
    style: 'currency',
    currency: 'ZAR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount);
};

export default function AdminPartnerProgram() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [dashboardData, setDashboardData] = useState(null);
  const [partners, setPartners] = useState([]);
  const [commissions, setCommissions] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('overview');
  
  // Modal state
  const [showCommissionModal, setShowCommissionModal] = useState(false);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [newCommissionRate, setNewCommissionRate] = useState(10);
  const [saving, setSaving] = useState(false);
  const [payoutRequests, setPayoutRequests] = useState([]);
  const [actionDialog, setActionDialog] = useState(null); // { req, mode: 'approve'|'reject' }
  const [actionNote, setActionNote] = useState('');
  const [actionSaving, setActionSaving] = useState(false);

  useEffect(() => {
    fetchData();
    fetchPayoutRequests();
  }, []);

  const fetchPayoutRequests = async () => {
    try {
      const token = localStorage.getItem('session_token');
      const r = await fetch(`${API_URL}/api/partner-program/admin/payout-requests`, {
        credentials: 'include',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (r.ok) setPayoutRequests(await r.json());
    } catch (e) {
      // silent
    }
  };

  const handlePayoutDecision = async () => {
    if (!actionDialog) return;
    setActionSaving(true);
    try {
      const token = localStorage.getItem('session_token');
      const endpoint = actionDialog.mode === 'approve' ? 'approve' : 'reject';
      const r = await fetch(`${API_URL}/api/partner-program/admin/payout-requests/${actionDialog.req.request_id}/${endpoint}`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
        body: JSON.stringify({ note: actionNote }),
      });
      if (!r.ok) {
        const err = await r.json().catch(() => ({}));
        throw new Error(err.detail || 'Action failed');
      }
      toast.success(`Payout ${actionDialog.mode === 'approve' ? 'approved & marked paid' : 'rejected'}`);
      setActionDialog(null);
      setActionNote('');
      fetchPayoutRequests();
      fetchData();
    } catch (e) {
      toast.error(e.message);
    } finally {
      setActionSaving(false);
    }
  };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [dashboardRes, partnersRes, commissionsRes] = await Promise.all([
        fetch(`${API_URL}/api/partner-program/admin/dashboard`, { credentials: 'include' }),
        fetch(`${API_URL}/api/partner-program/admin/partners`, { credentials: 'include' }),
        fetch(`${API_URL}/api/partner-program/admin/all-commissions?limit=50`, { credentials: 'include' }),
      ]);
      
      if (dashboardRes.ok) {
        const data = await dashboardRes.json();
        setDashboardData(data);
      }
      
      if (partnersRes.ok) {
        const data = await partnersRes.json();
        setPartners(data);
      }
      
      if (commissionsRes.ok) {
        const data = await commissionsRes.json();
        setCommissions(data);
      }
    } catch (error) {
      console.error('Error:', error);
      toast.error('Failed to load partner program data');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateCommissionRate = async () => {
    if (!selectedPartner) return;
    
    setSaving(true);
    try {
      const response = await fetch(
        `${API_URL}/api/partner-program/admin/partner/${selectedPartner.partner_id}/commission-rate`,
        {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({ commission_rate: newCommissionRate }),
        }
      );
      
      if (!response.ok) throw new Error('Failed to update commission rate');
      
      toast.success(`Commission rate updated to ${newCommissionRate}%`);
      setShowCommissionModal(false);
      fetchData();
    } catch (error) {
      toast.error('Failed to update commission rate');
    } finally {
      setSaving(false);
    }
  };

  const handleMarkPaid = async (commissionId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/partner-program/admin/commission/${commissionId}/mark-paid`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      
      if (!response.ok) throw new Error('Failed to mark as paid');
      
      toast.success('Commission marked as paid');
      fetchData();
    } catch (error) {
      toast.error('Failed to mark as paid');
    }
  };

  const handleMarkAllPaid = async (partnerId) => {
    try {
      const response = await fetch(
        `${API_URL}/api/partner-program/admin/partner/${partnerId}/mark-all-paid`,
        {
          method: 'POST',
          credentials: 'include',
        }
      );
      
      if (!response.ok) throw new Error('Failed to mark all as paid');
      
      const data = await response.json();
      toast.success(data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to mark all as paid');
    }
  };

  const handleExportPayouts = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/partner-program/admin/export-payouts`,
        { credentials: 'include' }
      );
      
      if (!response.ok) throw new Error('Failed to export');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `partner_payouts_${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report downloaded');
    } catch (error) {
      toast.error('Failed to export report');
    }
  };

  const filteredPartners = partners.filter(p => 
    p.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.referral_code?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const openCommissionModal = (partner) => {
    setSelectedPartner(partner);
    setNewCommissionRate(partner.commission_rate);
    setShowCommissionModal(true);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  const { overview, top_partners, monthly_trends } = dashboardData || {};

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" size="icon" onClick={() => navigate('/platform')} data-testid="back-btn">
                <ArrowLeft className="w-5 h-5" />
              </Button>
              <div>
                <h1 className="text-xl font-bold text-slate-900">Partner Program Management</h1>
                <p className="text-sm text-slate-500">Manage agent referrals and commissions</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={handleExportPayouts} data-testid="export-btn">
                <Download className="w-4 h-4 mr-2" />
                Export Payouts
              </Button>
              <Button variant="outline" size="sm" onClick={fetchData}>
                <RefreshCw className="w-4 h-4 mr-2" />
                Refresh
              </Button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-6">
          {['overview', 'partners', 'payouts', 'payout-requests'].map((tab) => (
            <Button
              key={tab}
              variant={activeTab === tab ? 'default' : 'outline'}
              size="sm"
              onClick={() => setActiveTab(tab)}
              data-testid={`tab-${tab}`}
            >
              {tab === 'payout-requests' ? 'Payout Requests' : tab.charAt(0).toUpperCase() + tab.slice(1)}
            </Button>
          ))}
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && (
          <>
            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="border-0 shadow-lg" data-testid="total-partners-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Users className="w-6 h-6 text-blue-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-slate-500 mb-1">Total Partners</h3>
                  <p className="text-3xl font-bold text-slate-900">{overview?.total_partners || 0}</p>
                  <p className="text-sm text-emerald-600 mt-1">{overview?.active_partners || 0} active</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg" data-testid="total-referrals-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center">
                      <Building2 className="w-6 h-6 text-emerald-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-slate-500 mb-1">Total Referrals</h3>
                  <p className="text-3xl font-bold text-slate-900">{overview?.total_referrals || 0}</p>
                  <p className="text-sm text-slate-500 mt-1">{overview?.total_units || 0} units</p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg" data-testid="subscription-revenue-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-purple-100 flex items-center justify-center">
                      <DollarSign className="w-6 h-6 text-purple-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-slate-500 mb-1">Partner Revenue</h3>
                  <p className="text-3xl font-bold text-slate-900">
                    {formatCurrency(overview?.total_subscription_revenue || 0)}
                  </p>
                </CardContent>
              </Card>

              <Card className="border-0 shadow-lg" data-testid="pending-payouts-card">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <div className="w-12 h-12 rounded-xl bg-amber-100 flex items-center justify-center">
                      <Wallet className="w-6 h-6 text-amber-600" />
                    </div>
                  </div>
                  <h3 className="text-sm font-medium text-slate-500 mb-1">Pending Payouts</h3>
                  <p className="text-3xl font-bold text-amber-600">
                    {formatCurrency(overview?.pending_payouts || 0)}
                  </p>
                  <p className="text-sm text-slate-500 mt-1">
                    Paid: {formatCurrency(overview?.paid_payouts || 0)}
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
              {/* Monthly Trends */}
              <Card className="border-0 shadow-lg" data-testid="trends-chart">
                <CardHeader>
                  <CardTitle className="text-lg">Monthly Commission Payouts</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="h-64">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={monthly_trends || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                        <XAxis dataKey="month" stroke="#64748b" />
                        <YAxis stroke="#64748b" tickFormatter={(v) => `R${(v/1000).toFixed(0)}k`} />
                        <Tooltip formatter={(value) => formatCurrency(value)} />
                        <Bar dataKey="commission" name="Commission" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </CardContent>
              </Card>

              {/* Top Partners */}
              <Card className="border-0 shadow-lg" data-testid="top-partners">
                <CardHeader>
                  <CardTitle className="text-lg">Top Partners</CardTitle>
                  <CardDescription>By total commission earned</CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {top_partners?.map((partner, i) => (
                      <div key={partner.partner_id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center text-white font-medium ${
                            i === 0 ? 'bg-amber-500' : i === 1 ? 'bg-slate-400' : i === 2 ? 'bg-amber-700' : 'bg-slate-300'
                          }`}>
                            {i + 1}
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{partner.company_name}</p>
                            <p className="text-sm text-slate-500">{partner.referral_code}</p>
                          </div>
                        </div>
                        <p className="font-bold text-slate-900">
                          {formatCurrency(partner.total_commission || 0)}
                        </p>
                      </div>
                    ))}
                    {(!top_partners || top_partners.length === 0) && (
                      <p className="text-center text-slate-500 py-4">No partner data yet</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        {/* Partners Tab */}
        {activeTab === 'partners' && (
          <Card className="border-0 shadow-lg" data-testid="partners-table">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Partner Accounts</CardTitle>
                  <CardDescription>Manage partner commission rates and status</CardDescription>
                </div>
                <div className="relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                  <Input
                    placeholder="Search partners..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-9 w-64"
                    data-testid="search-partners"
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Partner</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Code</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Rate</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Referrals</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Revenue</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Pending</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredPartners.map((partner) => (
                      <tr key={partner.partner_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{partner.company_name}</p>
                        </td>
                        <td className="py-3 px-4">
                          <code className="px-2 py-1 bg-slate-100 rounded text-sm">{partner.referral_code}</code>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className="font-medium">{partner.commission_rate}%</span>
                        </td>
                        <td className="py-3 px-4 text-right">{partner.stats?.total_complexes || 0}</td>
                        <td className="py-3 px-4 text-right font-medium">
                          {formatCurrency(partner.stats?.total_revenue || 0)}
                        </td>
                        <td className="py-3 px-4 text-right text-amber-600 font-medium">
                          {formatCurrency(partner.stats?.pending_commission || 0)}
                        </td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            partner.status === 'active' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : partner.status === 'suspended'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {partner.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          <div className="flex justify-center gap-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openCommissionModal(partner)}
                              data-testid={`edit-rate-${partner.partner_id}`}
                            >
                              <Percent className="w-3 h-3 mr-1" />
                              Rate
                            </Button>
                            {partner.stats?.pending_commission > 0 && (
                              <Button 
                                variant="outline" 
                                size="sm"
                                onClick={() => handleMarkAllPaid(partner.partner_id)}
                                data-testid={`pay-all-${partner.partner_id}`}
                              >
                                <CheckCircle2 className="w-3 h-3 mr-1" />
                                Pay All
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {filteredPartners.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No partners found</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payouts Tab */}
        {activeTab === 'payouts' && (
          <Card className="border-0 shadow-lg" data-testid="payouts-table">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="text-lg">Commission Payouts</CardTitle>
                  <CardDescription>Track and manage partner payments</CardDescription>
                </div>
                <Button variant="outline" onClick={handleExportPayouts}>
                  <FileDown className="w-4 h-4 mr-2" />
                  Export CSV
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Partner</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Complex</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Subscription</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Rate</th>
                      <th className="text-right py-3 px-4 text-sm font-medium text-slate-500">Commission</th>
                      <th className="text-left py-3 px-4 text-sm font-medium text-slate-500">Period</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Status</th>
                      <th className="text-center py-3 px-4 text-sm font-medium text-slate-500">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {commissions.map((commission) => (
                      <tr key={commission.commission_id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{commission.partner_name}</p>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{commission.hoa_name}</td>
                        <td className="py-3 px-4 text-right">{formatCurrency(commission.subscription_amount)}</td>
                        <td className="py-3 px-4 text-center">{commission.commission_rate}%</td>
                        <td className="py-3 px-4 text-right font-medium text-emerald-600">
                          {formatCurrency(commission.commission_amount)}
                        </td>
                        <td className="py-3 px-4 text-slate-500">{commission.period}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            commission.status === 'paid' 
                              ? 'bg-emerald-100 text-emerald-700' 
                              : commission.status === 'pending'
                              ? 'bg-amber-100 text-amber-700'
                              : 'bg-slate-100 text-slate-600'
                          }`}>
                            {commission.status}
                          </span>
                        </td>
                        <td className="py-3 px-4 text-center">
                          {commission.status === 'pending' && (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => handleMarkPaid(commission.commission_id)}
                            >
                              <CheckCircle2 className="w-3 h-3 mr-1" />
                              Mark Paid
                            </Button>
                          )}
                          {commission.status === 'paid' && (
                            <span className="text-sm text-slate-400">
                              {commission.paid_at ? new Date(commission.paid_at).toLocaleDateString() : '-'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {commissions.length === 0 && (
                  <p className="text-center text-slate-500 py-8">No commission records yet</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Payout Requests Tab */}
        {activeTab === 'payout-requests' && (
          <Card className="border-0 shadow-lg" data-testid="payout-requests-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Wallet className="w-5 h-5 text-indigo-600" />
                Partner Payout Requests
              </CardTitle>
              <CardDescription>Review and approve partner-initiated payout requests</CardDescription>
            </CardHeader>
            <CardContent>
              {payoutRequests.length === 0 ? (
                <p className="text-center text-slate-500 py-8">No payout requests yet.</p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-2 text-slate-500">Partner</th>
                        <th className="text-right py-3 px-2 text-slate-500">Amount</th>
                        <th className="text-center py-3 px-2 text-slate-500">Commissions</th>
                        <th className="text-center py-3 px-2 text-slate-500">Method</th>
                        <th className="text-left py-3 px-2 text-slate-500">Requested</th>
                        <th className="text-left py-3 px-2 text-slate-500">Status</th>
                        <th className="text-center py-3 px-2 text-slate-500">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {payoutRequests.map((req) => (
                        <tr key={req.request_id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`payout-request-row-${req.request_id}`}>
                          <td className="py-3 px-2">
                            <div className="font-medium text-slate-900">{req.company_name}</div>
                            {req.notes && <div className="text-xs text-slate-500 mt-1 max-w-xs truncate">{req.notes}</div>}
                          </td>
                          <td className="py-3 px-2 text-right font-semibold text-emerald-600">{formatCurrency(req.total_amount)}</td>
                          <td className="py-3 px-2 text-center">{req.commission_count}</td>
                          <td className="py-3 px-2 text-center capitalize">{req.payment_method?.replace('_', ' ')}</td>
                          <td className="py-3 px-2 text-slate-500 text-xs">{new Date(req.created_at).toLocaleString()}</td>
                          <td className="py-3 px-2">
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                              req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' :
                              req.status === 'rejected' ? 'bg-red-100 text-red-700' :
                              'bg-amber-100 text-amber-700'
                            }`}>
                              {req.status === 'pending_approval' ? 'Pending' : req.status}
                            </span>
                          </td>
                          <td className="py-3 px-2 text-center">
                            {req.status === 'pending_approval' ? (
                              <div className="flex gap-1 justify-center">
                                <Button size="sm" onClick={() => setActionDialog({ req, mode: 'approve' })} className="bg-emerald-600 hover:bg-emerald-700" data-testid={`approve-${req.request_id}`}>
                                  <CheckCircle2 className="w-3 h-3 mr-1" /> Approve
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => setActionDialog({ req, mode: 'reject' })} className="text-red-600" data-testid={`reject-${req.request_id}`}>
                                  Reject
                                </Button>
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">
                                {req.approved_at ? new Date(req.approved_at).toLocaleDateString() :
                                 req.rejected_at ? new Date(req.rejected_at).toLocaleDateString() : '-'}
                              </span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </main>

      {/* Commission Rate Modal */}
      <Dialog open={showCommissionModal} onOpenChange={setShowCommissionModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Update Commission Rate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <p className="text-slate-600">
              Update commission rate for <strong>{selectedPartner?.company_name}</strong>
            </p>
            <div>
              <Label>Commission Rate (%)</Label>
              <Input
                type="number"
                min={0}
                max={50}
                step={0.5}
                value={newCommissionRate}
                onChange={(e) => setNewCommissionRate(parseFloat(e.target.value) || 0)}
                data-testid="commission-rate-input"
              />
              <p className="text-sm text-slate-500 mt-1">
                Default rate is 10%. Range: 0-50%
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCommissionModal(false)}>Cancel</Button>
            <Button onClick={handleUpdateCommissionRate} disabled={saving} data-testid="save-commission-rate">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payout Decision Dialog */}
      <Dialog open={!!actionDialog} onOpenChange={(o) => { if (!o) { setActionDialog(null); setActionNote(''); } }}>
        <DialogContent data-testid="payout-decision-dialog">
          <DialogHeader>
            <DialogTitle>
              {actionDialog?.mode === 'approve' ? 'Approve Payout' : 'Reject Payout'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="p-3 bg-slate-50 rounded-lg text-sm">
              <div><strong>Partner:</strong> {actionDialog?.req.company_name}</div>
              <div><strong>Amount:</strong> {actionDialog && formatCurrency(actionDialog.req.total_amount)}</div>
              <div><strong>Commissions:</strong> {actionDialog?.req.commission_count}</div>
              <div><strong>Method:</strong> {actionDialog?.req.payment_method?.replace('_', ' ')}</div>
              {actionDialog?.req.notes && <div className="mt-2 text-slate-600">"{actionDialog.req.notes}"</div>}
            </div>
            <div>
              <Label>{actionDialog?.mode === 'approve' ? 'Approval Note (optional)' : 'Reason for Rejection'}</Label>
              <Input
                value={actionNote}
                onChange={(e) => setActionNote(e.target.value)}
                placeholder={actionDialog?.mode === 'approve' ? 'EFT reference, transaction ID, etc.' : 'e.g. Bank details incomplete'}
                data-testid="payout-action-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setActionDialog(null); setActionNote(''); }}>Cancel</Button>
            <Button
              onClick={handlePayoutDecision}
              disabled={actionSaving}
              className={actionDialog?.mode === 'approve' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="payout-action-confirm"
            >
              {actionSaving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              {actionDialog?.mode === 'approve' ? 'Approve & Mark Paid' : 'Confirm Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
